'use client';

import { useId, useState } from 'react';

import cx from 'classnames';

import { type Masked, unmask } from '~/contentful/graphql';
import { subscribe } from '~/ui/actions/newsletter';
import { ActionError } from '~/ui/actions/types';
import CheckIcon from '~/ui/assets/icons/check.svg';
import { Event, trackEvent } from '~/ui/components/analytics';
import { Column, type CommonColumnProps } from '~/ui/components/grid';
import { Link } from '~/ui/components/link';
import { Breakpoint } from '~/ui/styles/grid';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { Route } from '~/ui/systems/internalLink/route';
import { EMAIL_REGEXP } from '~/ui/validations/text';
import { FieldText } from '~/v1/components/fields/text/text';

import styles from './newsletter.module.scss';
import { type Newsletter_NewsletterFragment } from './query';

enum SubscriptionStatus {
  DEFAULT = 'default',
  LOADING = 'loading',
  INVALID = 'invalid',
  ERROR = 'error',
  SUCCESS = 'success',
  ALREADY_SUBSCRIBED = 'alreadySubscribed',
}

const ERROR_TEXTS = {
  [SubscriptionStatus.INVALID]: 'Enter a valid email format',
  [SubscriptionStatus.ERROR]: 'Something went wrong. Check your connection or try again later.',
  [SubscriptionStatus.ALREADY_SUBSCRIBED]: "You're already on our mailing list.",
};

export interface NewsletterProps {
  newsletter: Masked<typeof Newsletter_NewsletterFragment>;

  disclaimerLinkHref?: string;
  disclaimerLinkLabel?: string;

  inputLabel?: string;
  actionLabel?: string;

  isInFooter?: boolean;
  noPadding?: boolean;
  onSubmit?: (email: string) => Promise<Response>;

  className?: string;
}

export function Newsletter({
  newsletter: parentNewsletter,
  disclaimerLinkHref = Route.PRIVACY_POLICY,
  disclaimerLinkLabel = "Mellon Foundation's Privacy Policy",
  inputLabel = 'Your email',
  actionLabel = 'Subscribe',
  isInFooter,
  noPadding,
  className,
}: NewsletterProps) {
  const { description, disclaimer } =
    unmask<typeof Newsletter_NewsletterFragment>(parentNewsletter);

  const id = useId();
  const [status, setStatus] = useState(SubscriptionStatus.DEFAULT);

  async function submit(email: string) {
    setStatus(SubscriptionStatus.LOADING);

    const res = await subscribe(email);

    if (res?.success) {
      setStatus(SubscriptionStatus.SUCCESS);
      return;
    }

    switch (res?.error) {
      case ActionError.INVALID_EMAIL:
        setStatus(SubscriptionStatus.INVALID);
        break;
      case ActionError.ALREADY_SUBSCRIBED:
        setStatus(SubscriptionStatus.ALREADY_SUBSCRIBED);
        break;
      default:
        if (res) {
          setStatus(SubscriptionStatus.ERROR);
        }
        break;
    }
    trackEvent(Event.NEWSLETTER_SUBMIT);
  }

  return (
    <div
      className={cx(styles.newsletter, className, {
        [styles.newsletterWithoutPadding]: noPadding,
        [styles.newsletterFooter]: isInFooter,
      })}
    >
      {status === SubscriptionStatus.SUCCESS ? (
        <div aria-live="polite">
          <CheckIcon className={styles.checkmarkIcon} />
          <p className={typographyStyles.bodyLarge}>Thank you for subscribing!</p>
          <p className={typographyStyles.bodyLarge}>We&rsquo;ve sent you a confirmation email.</p>
        </div>
      ) : (
        <>
          <p className={cx(typographyStyles.bodyLarge, styles.title)}>{description}</p>
          <FieldText
            className={styles.textInput}
            id={id}
            label={inputLabel}
            buttonLabel={actionLabel}
            validationRegEx={EMAIL_REGEXP}
            helperText="Enter a valid email format"
            handleFocus={() => trackEvent(Event.NEWSLETTER_FOCUS)}
            onActionClick={submit}
            isLoading={status === SubscriptionStatus.LOADING}
            isThemed={isInFooter}
            errorText={ERROR_TEXTS[status as keyof typeof ERROR_TEXTS]}
          />

          <p className={cx(typographyStyles.caption, styles.disclaimer)}>
            {disclaimer}
            &nbsp;
            <Link
              href={disclaimerLinkHref}
              className={cx(typographyStyles.caption, styles.disclaimerLink)}
            >
              {disclaimerLinkLabel}
            </Link>
            .
          </p>
        </>
      )}
    </div>
  );
}

Newsletter.Column = function NewsletterColumn({
  hasSibling,
  ...props
}: { hasSibling: boolean } & CommonColumnProps) {
  return (
    <Column
      {...props}
      span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 3, [Breakpoint.DESKTOP]: 4 }}
      offsetLeft={hasSibling ? undefined : { [Breakpoint.TABLET]: 4, [Breakpoint.DESKTOP]: 5 }}
    />
  );
};
